import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { Code2, BrainCircuit, Palette, Layers, Workflow, Blocks } from "lucide-react";
import { useLenis } from "@/components/SmoothScroll";
import Reveal from "@/components/Reveal";
import "./Services.css";
import SectionKicker from "@/components/SectionKicker";
import CTASection from "@/components/CTASection";
import TextScramble from "@/components/TextScramble";
import WaveText from "@/components/WaveText";
import ScrollConnectedPath, { PathNode } from "@/components/ScrollConnectedPath";

const EASE = [0.22, 1, 0.36, 1];

const services = [
  {
    icon: Code2,
    tag: "01 — Build",
    title: "Custom Software & Application Development",
    desc: "We design and engineer products end-to-end — high-performance web apps, polished native and cross-platform mobile apps, and bespoke enterprise software that automates operations and removes manual bottlenecks.",
    points: ["Responsive web applications", "Native & cross-platform mobile", "Bespoke enterprise software", "Workflow-driven platforms"],
  },
  {
    icon: Layers,
    tag: "02 — Engineer",
    title: "Frontend & Backend Engineering",
    desc: "Production-ready interfaces built to an ultra-minimal, futuristic design system, backed by secure, high-performance APIs and scalable server-side infrastructure capable of heavy, real-time workloads.",
    points: ["Pixel-perfect frontend", "Secure high-performance APIs", "Scalable backend architecture", "Real-time systems"],
  },
  {
    icon: BrainCircuit,
    tag: "03 — Intelligence",
    title: "Artificial Intelligence & Machine Learning",
    desc: "From custom AI architectures to rigorous training and fine-tuning for accuracy and low latency, we embed intelligent systems seamlessly into your software and workflows.",
    points: ["Custom AI models", "Training & fine-tuning", "Low-latency inference", "Seamless AI integration"],
  },
  {
    icon: Blocks,
    tag: "04 — Blockchain",
    title: "Blockchain & Web3 Engineering",
    desc: "We engineer smart contracts, decentralised applications and Web3 integrations with a security-first approach — from token standards and DeFi mechanics to wallet integrations and on-chain/off-chain architecture.",
    points: ["Smart contract development & audit readiness", "dApp & DeFi engineering", "Token standards & NFT infrastructure", "Wallet & Web3 API integrations"],
  },
  {
    icon: Workflow,
    tag: "05 — Scale",
    title: "Infrastructure, Pipelines & Automation",
    desc: "Robust CI/CD pipelines for rapid, error-free releases, workflow automation that removes repetitive toil, and scalable, monitored, highly available cloud architectures.",
    points: ["Automated CI/CD pipelines", "Workflow automation", "DevOps & cloud infrastructure", "Monitoring & high availability"],
  },
  {
    icon: Palette,
    tag: "06 — Craft",
    title: "UI/UX Design",
    desc: "End-to-end user journey mapping, wireframing and prototyping paired with dark-themed, futuristic, ultra-minimal interfaces that prioritise focus and aesthetic precision.",
    points: ["Product design & journeys", "Wireframing & prototyping", "Ultra-minimal UI systems", "Dark, futuristic aesthetics"],
  },
];

function ServiceBlock({ s, i }) {
  const ref = useRef(null);
  const kickerRef = useRef(null);
  const reduced = useReducedMotion();
  // Drives the pipeline node light-up and the active-heading lift.
  // amount 0.2: the node lights as the block meaningfully enters, not
  // after half of a tall block is visible.
  const active = useInView(ref, { amount: 0.2 });

  // Anchor the rail node beside this block's kicker line ("01 — Build"),
  // not the block's vertical center. Uses the offsetTop chain rather than
  // getBoundingClientRect so the entrance reveal's transient y-transform
  // doesn't skew the measurement. Re-measure after brand fonts settle.
  const [nodeTop, setNodeTop] = useState(null);
  useLayoutEffect(() => {
    const measure = () => {
      let el = kickerRef.current;
      const block = ref.current;
      if (!el || !block) return;
      let top = el.offsetHeight / 2;
      while (el && el !== block) {
        top += el.offsetTop;
        el = el.offsetParent;
      }
      setNodeTop(top);
    };
    measure();
    let mounted = true;
    document.fonts.ready.then(() => { if (mounted) measure(); });
    window.addEventListener("resize", measure);
    return () => { mounted = false; window.removeEventListener("resize", measure); };
  }, []);

  return (
    <div
      ref={ref}
      id={`service-${i + 1}`}
      className="service-detail scroll-mt-32"
      data-testid={`service-block-${i}`}
    >
      <PathNode index={i + 1} active={active} top={nodeTop} />
      <Reveal className="service-detail-copy">
        <p ref={kickerRef} className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-400">{s.tag}</p>
        <motion.h2
          tabIndex={-1}
          className="service-detail-title font-heading rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-8 focus-visible:outline-cyan-accent"
          animate={{ opacity: active || reduced ? 1 : 0.85 }}
          transition={{ duration: reduced ? 0 : 0.5 }}
        >
          {reduced ? s.title : <TextScramble text={s.title} trigger="view" duration={550} />}
        </motion.h2>
        <p className="service-detail-description">{s.desc}</p>
      </Reveal>
      <Reveal delay={reduced ? 0 : 0.08}>
        <div className="service-capabilities">
          <div className="service-capabilities-label">
            <s.icon aria-hidden="true" focusable="false" size={24} strokeWidth={1.5} />
            <h3 id={`capabilities-${i + 1}`}>Capabilities</h3>
          </div>
          <ul aria-labelledby={`capabilities-${i + 1}`}>
            {s.points.map(p => (
              <li key={p}><span className="service-capability-dot" aria-hidden="true" /><span>{p}</span></li>
            ))}
          </ul>
        </div>
      </Reveal>
    </div>
  );
}

export default function Services() {
  const location = useLocation();
  const navigate = useNavigate();
  const lenis = useLenis();
  const reduced = useReducedMotion();

  useEffect(() => {
    if (!/^#service-[1-6]$/.test(location.hash)) return;
    const heading = document.querySelector(`${location.hash} h2`);
    if (!heading) return;
    // Layout offsets exclude Reveal's temporary transform. Centring a whole
    // mobile block can leave its heading offscreen; arrive below the header.
    let top = 0;
    for (let node = heading; node; node = node.offsetParent) top += node.offsetTop;
    // Back/Forward restores native fragment focus after popstate. Apply our
    // accessible heading destination on the following frame, not before it.
    const frame = requestAnimationFrame(() => {
      heading.focus({ preventScroll: true });
      if (lenis) lenis.scrollTo(Math.max(0, top - 112), { immediate: !!reduced, force: true });
      else window.scrollTo({ top: Math.max(0, top - 112), behavior: reduced ? 'instant' : 'smooth' });
    });
    return () => cancelAnimationFrame(frame);
  }, [location.hash, location.key, lenis, reduced]);

  const visitService = (event, hash) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate({ hash }, { preventScrollReset: true });
  };

  return (
    <div data-testid="services-page" className="services-page">
      <div className="grain services-page-grain" aria-hidden="true" />
      {/* HERO */}
      <section className="relative pt-28 pb-8 md:pt-36 md:pb-12 overflow-hidden">
        <div className="tech-grid grid-fade absolute inset-0 opacity-40" />
        <div className="glow-orb animate-pulse-glow absolute -top-10 right-[15%] h-[400px] w-[400px]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionKicker index="[ 02 ]">Services</SectionKicker>
          </Reveal>
          <Reveal delay={0.1} variant="mask">
            {/* text-4xl below sm: the nowrap WaveText word "ENGINEERING," cannot
                wrap internally and overflows 375px screens at text-5xl */}
            <h1 className="mt-6 font-heading font-black uppercase tracking-normal text-4xl sm:text-5xl md:text-7xl lg:text-8xl text-white leading-[0.9] max-w-5xl">
              <span className="sr-only">Custom software development services — </span>
              <WaveText text="End-to-end engineering, one partner." />
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-zinc-400 text-lg leading-relaxed">
              Custom software development, AI development, blockchain engineering
              and DevOps automation services covering the full lifecycle — from
              custom apps and machine learning to smart contracts, backend
              engineering and ultra-minimal design — so you ship faster with a
              single partner.
            </p>
          </Reveal>
          
          {/* Quick Navigation */}
          <Reveal delay={0.3}>
            <nav aria-label="Explore services" className="mt-8 flex flex-wrap gap-3">
              {services.map((s, i) => (
                <a
                  key={i}
                  href={`#service-${i + 1}`}
                  aria-label={`${s.tag.split('—')[1].trim()}: ${s.title}`}
                  aria-current={location.hash === `#service-${i + 1}` ? 'location' : undefined}
                  onClick={event => visitService(event, `#service-${i + 1}`)}
                  className="inline-flex items-center gap-2 min-h-11 px-4 py-2 rounded-full border border-white/10 bg-surface/50 backdrop-blur-sm text-sm text-zinc-300 hover:border-cyan-accent/50 hover:text-cyan-accent aria-[current=location]:border-cyan-accent/50 aria-[current=location]:text-cyan-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cyan-accent transition-colors duration-200"
                >
                  <s.icon aria-hidden="true" className="h-4 w-4 text-cyan-accent" />
                  {s.tag.split('—')[1].trim()}
                </a>
              ))}
            </nav>
          </Reveal>

          {/* Page-intro hairline draws beneath the header */}
          <motion.div
            className="mt-10 h-px w-full origin-left bg-white/10"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.5 }}
          />
        </div>
      </section>

      {/* Existing connected rail, with content-led service panels. */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollConnectedPath trimToNodes>
            <div className="service-details">
              {services.map((s, i) => (
                <ServiceBlock key={s.title} s={s} i={i} />
              ))}
            </div>
          </ScrollConnectedPath>
        </div>
      </section>

      <CTASection eyebrow="Have a project in mind?" title="Tell us what you're building." />
    </div>
  );
}
